import { useMemo, useState } from 'react';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { CHART_STYLES } from '../../chartTheme';
// ── Types ──────────────────────────────────────────────────────────────

type RowDimension = 'Wells' | 'Analytes';
type ColDimension = 'Analytes' | 'Date (Quarter)';
type AggMethod = 'Latest' | 'Mean' | 'Max' | 'Min';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Convert mg/L base value to display unit */
function convertConc(value: number, unit: ConcentrationUnit): number {
  return value;
}

/** Format a date string into a quarter label (e.g. "2025-Q1") */
function toQuarter(dateStr: string): string {
  const d = new Date(dateStr);
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `${d.getFullYear()}-Q${q}`;
}

/** Aggregate an array of numbers by method */
function aggregate(values: number[], method: AggMethod): number | null {
  if (values.length === 0) return null;
  switch (method) {
    case 'Latest':
      return values[values.length - 1];
    case 'Mean':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'Max':
      return Math.max(...values);
    case 'Min':
      return Math.min(...values);
  }
}

/** Interpolate between blue (low) and red (high) based on t in [0,1] */
function heatColor(t: number): string {
  // low = #3b82f6 at 10% opacity, high = #ef4444 at 60% opacity
  const r = Math.round(59 + (239 - 59) * t);
  const g = Math.round(130 + (68 - 130) * t);
  const b = Math.round(246 + (68 - 246) * t);
  const a = 0.1 + 0.5 * t;
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
}

// ── Pivot logic ────────────────────────────────────────────────────────

interface PivotResult {
  rowKeys: string[];
  colKeys: string[];
  cells: Map<string, number | null>; // "row||col" -> value
  min: number;
  max: number;
}

function buildPivot(
  data: AnalyticalTimeseriesResponse,
  rowDim: RowDimension,
  colDim: ColDimension,
  aggMethod: AggMethod,
  unit: ConcentrationUnit,
): PivotResult {
  // Collect raw values keyed by (row, col) -> number[]
  const buckets = new Map<string, number[]>();
  const rowSet = new Set<string>();
  const colSet = new Set<string>();

  for (const well of data.wells) {
    for (const [compound, analyte] of Object.entries(well.analytes)) {
      // Sort measurements by date for "Latest" to work correctly
      const sorted = [...analyte.measurements].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

      if (colDim === 'Date (Quarter)') {
        // Group measurements by quarter
        const qBuckets = new Map<string, number[]>();
        for (const m of sorted) {
          if (m.concentration == null) continue;
          const q = toQuarter(m.date);
          if (!qBuckets.has(q)) qBuckets.set(q, []);
          qBuckets.get(q)!.push(m.concentration);
        }

        for (const [quarter, vals] of qBuckets) {
          const rowKey = rowDim === 'Wells' ? well.well_name : compound;
          const colKey = quarter;
          rowSet.add(rowKey);
          colSet.add(colKey);
          const key = `${rowKey}||${colKey}`;
          if (!buckets.has(key)) buckets.set(key, []);
          buckets.get(key)!.push(...vals);
        }
      } else {
        // colDim === 'Analytes'
        const rowKey = rowDim === 'Wells' ? well.well_name : compound;
        const colKey = rowDim === 'Wells' ? compound : well.well_name;
        rowSet.add(rowKey);
        colSet.add(colKey);
        const key = `${rowKey}||${colKey}`;
        if (!buckets.has(key)) buckets.set(key, []);
        for (const m of sorted) {
          if (m.concentration != null) {
            buckets.get(key)!.push(m.concentration);
          }
        }
      }
    }
  }

  // Aggregate and convert units
  const cells = new Map<string, number | null>();
  let globalMin = Infinity;
  let globalMax = -Infinity;

  for (const [key, vals] of buckets) {
    const raw = aggregate(vals, aggMethod);
    const converted = raw != null ? convertConc(raw, unit) : null;
    cells.set(key, converted);
    if (converted != null) {
      if (converted < globalMin) globalMin = converted;
      if (converted > globalMax) globalMax = converted;
    }
  }

  const rowKeys = [...rowSet].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const colKeys = [...colSet].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return {
    rowKeys,
    colKeys,
    cells,
    min: globalMin === Infinity ? 0 : globalMin,
    max: globalMax === -Infinity ? 0 : globalMax,
  };
}

// ── Dropdown component ─────────────────────────────────────────────────

function Selector<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--dv-text-primary)' }}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="rounded-md px-2 py-1 text-xs outline-none"
        style={{
          background: 'var(--dv-bg-input)',
          border: '1px solid var(--dv-border-default)',
          color: 'var(--dv-text-primary)',
        }}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export function PivotHeatmap({ data, unit }: Props) {
  void CHART_STYLES; // acknowledge import (used for theme consistency reference)

  const [rowDim, setRowDim] = useState<RowDimension>('Wells');
  const [colDim, setColDim] = useState<ColDimension>('Analytes');
  const [aggMethod, setAggMethod] = useState<AggMethod>('Latest');

  const pivot = useMemo(
    () => buildPivot(data, rowDim, colDim, aggMethod, unit),
    [data, rowDim, colDim, aggMethod, unit],
  );

  const isEmpty = pivot.rowKeys.length === 0 || pivot.colKeys.length === 0;
  const range = pivot.max - pivot.min;

  return (
      <div
        role="img"
        aria-label="Pivot heatmap of configurable data dimensions"
      >
        <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
          Configurable pivot heatmap — choose any combination of rows, columns, and values to create a custom data view. Useful for exploring patterns across multiple dimensions.
        </p>
        {/* Selectors */}
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <Selector label="Rows:" value={rowDim} options={['Wells', 'Analytes'] as const} onChange={setRowDim} />
          <Selector
            label="Columns:"
            value={colDim}
            options={['Analytes', 'Date (Quarter)'] as const}
            onChange={setColDim}
          />
          <Selector
            label="Value:"
            value={aggMethod}
            options={['Latest', 'Mean', 'Max', 'Min'] as const}
            onChange={setAggMethod}
          />
        </div>

        {isEmpty ? (
          <div
            className="flex h-48 items-center justify-center text-xs font-semibold tracking-widest"
            style={{ color: 'var(--dv-text-primary)' }}
          >
            NO ANALYTICAL DATA AVAILABLE
          </div>
        ) : (
          <div className="overflow-auto" style={{ maxHeight: 520 }}>
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th
                    className="sticky left-0 top-0 z-20 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ background: 'var(--dv-frosted-bg)', color: 'var(--dv-text-primary)' }}
                  >
                    {rowDim === 'Wells' ? 'Well' : 'Analyte'}
                  </th>
                  {pivot.colKeys.map((col) => (
                    <th
                      key={col}
                      className="sticky top-0 z-10 whitespace-nowrap px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
                      style={{ background: 'var(--dv-frosted-bg)', color: 'var(--dv-text-primary)' }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pivot.rowKeys.map((row, ri) => (
                  <tr
                    key={row}
                    style={{
                      background: ri % 2 === 1 ? 'var(--dv-table-row-alt)' : undefined,
                    }}
                    className="transition-colors"
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'var(--dv-table-row-hover)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background =
                        ri % 2 === 1 ? 'var(--dv-table-row-alt)' : '';
                    }}
                  >
                    <td
                      className="sticky left-0 z-10 whitespace-nowrap px-3 py-1.5 text-[10px] font-medium"
                      style={{ background: 'var(--dv-frosted-bg)', color: 'var(--dv-text-primary)' }}
                    >
                      {row}
                    </td>
                    {pivot.colKeys.map((col) => {
                      const val = pivot.cells.get(`${row}||${col}`) ?? null;
                      const t = val != null && range > 0 ? (val - pivot.min) / range : 0;
                      return (
                        <td
                          key={col}
                          className="px-3 py-1.5 text-center font-mono text-[10px]"
                          style={{
                            background: val != null ? heatColor(t) : undefined,
                            color: 'var(--dv-text-primary)',
                          }}
                        >
                          {val != null ? val.toFixed(3) : '\u2014'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
